import type { PatientProfileSearchResult } from '../../../contracts';

/**
 * The confidence rule that decides whether the dropdown should pre-highlight
 * a single match so Enter silently links it.
 *
 * Three conditions must all hold:
 *   1. Exactly one candidate is returned by search.
 *   2. The typed string is at least two whitespace-separated tokens
 *      (i.e. the user typed both first and last name). Single-token
 *      names are too collision-prone — Ryan's data shows a 7.1% name
 *      collision rate for single-word names vs 2.0% for full names.
 *   3. The candidate's full name (first + last) matches the typed string
 *      after case- and whitespace-normalisation.
 *
 * When this returns a result, the FE should pre-highlight that row and
 * commit-on-Enter links it. Otherwise the user must arrow/Tab to a row
 * deliberately, or click the explicit Create CTA.
 */

export function pickAutoLinkCandidate(
  query: string,
  candidates: readonly PatientProfileSearchResult[]
): PatientProfileSearchResult | null {
  if (candidates.length !== 1) return null;

  const tokens = normalize(query).split(' ').filter(Boolean);
  if (tokens.length < 2) return null;

  const candidate = candidates[0];
  if (!candidate) return null;
  const fullName = normalize(
    [candidate.first_name, candidate.last_name].filter(Boolean).join(' ')
  );
  if (!fullName) return null;

  return fullName === tokens.join(' ') ? candidate : null;
}

function normalize(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}
